import React, { useState, useEffect } from 'react';
import MatchCard from '../components/MatchCard';
import { MatchGridSkeleton } from '../components/MatchCardSkeleton';
import { useAuth } from '../auth/useAuth';
import { API_BASE_URL } from '../config';
import './SportsPage.css';

const sportCategories = [
  { icon: '⚽', label: 'Football'   },
  { icon: '🏀', label: 'Basketball' },
  { icon: '🎾', label: 'Tennis'     },
  { icon: '🏈', label: 'Américain'  },
  { icon: '🏒', label: 'Hockey'     },
  { icon: '🎮', label: 'eSports'    },
  { icon: '🏉', label: 'Rugby'      },
];

export default function SportsPage({ addBet, bets, selectedLeague }) {
  const { accessToken } = useAuth();

  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState('');
  const [sortBy, setSortBy]     = useState('date');

  const authHeaders = accessToken
    ? { Authorization: `Bearer ${accessToken}` }
    : {};

  // Fetch today's matches on mount; re-fetch when league or token changes
  useEffect(() => {
    setLoading(true);
    setError(null);

    const url = selectedLeague
      ? `${API_BASE_URL}/api/v1/fixtures?league=${encodeURIComponent(selectedLeague)}&limit=200`
      : `${API_BASE_URL}/api/v1/fixtures?status=NS&limit=200`;

    fetch(url, { credentials: 'include', headers: authHeaders })
      .then((res) => {
        if (!res.ok) throw new Error(`Erreur ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setFixtures(Array.isArray(data) ? data : (data.fixtures ?? []));
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLeague, accessToken]);

  const now = new Date();

  const filtered = fixtures.filter((m) => {
    // When a league is selected, show only upcoming + live matches (not past FT)
    if (selectedLeague) {
      const isLive = ['LIVE', '1H', '2H', 'HT'].includes(m.status);
      if (!isLive && m.match_date && new Date(m.match_date) < now) return false;
    }
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      m.home_team?.toLowerCase().includes(q) ||
      m.away_team?.toLowerCase().includes(q) ||
      m.league?.toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'odds_asc')  return (a.odds?.[0]?.odd_value ?? 0) - (b.odds?.[0]?.odd_value ?? 0);
    if (sortBy === 'odds_desc') return (b.odds?.[0]?.odd_value ?? 0) - (a.odds?.[0]?.odd_value ?? 0);
    return new Date(a.match_date) - new Date(b.match_date);
  });

  return (
    <div className="sports-page">
      <div className="page-header">
        <h1 className="page-title">⚽ Paris Sportifs</h1>
        <p className="page-subtitle">
          {selectedLeague ? `Matchs — ${selectedLeague}` : "Matchs d'aujourd'hui"}
        </p>
      </div>

      {/* Category pills */}
      <div className="category-scroll">
        {sportCategories.map(({ icon, label }) => (
          <button key={label} className="category-pill">
            {icon} {label}
          </button>
        ))}
      </div>

      {/* Search + sort */}
      <div className="sports-controls">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Rechercher un match, une équipe..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="search-clear" onClick={() => setSearch('')}>×</button>}
        </div>
        <select className="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="date">Trier: Date</option>
          <option value="odds_asc">Trier: Cotes ↑</option>
          <option value="odds_desc">Trier: Cotes ↓</option>
        </select>
      </div>

      {/* Loading */}
      {loading && <MatchGridSkeleton count={6} />}

      {/* Error */}
      {error && !loading && (
        <div className="sports-fetch-error">⚠️ {error}</div>
      )}

      {/* Results */}
      {!loading && !error && (
        <>
          <div className="results-info">
            {sorted.length} match{sorted.length !== 1 ? 's' : ''} disponible{sorted.length !== 1 ? 's' : ''}
          </div>

          {sorted.length === 0 ? (
            <div className="no-results">
              <span className="no-results-icon">🔍</span>
              <p>Aucun match trouvé</p>
              {search && (
                <button onClick={() => setSearch('')}>Réinitialiser la recherche</button>
              )}
            </div>
          ) : (
            <div className="sports-matches-grid">
              {sorted.map((match) => (
                <MatchCard key={match.id} match={match} addBet={addBet} bets={bets} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
